// AdminAuditIntakesPage.js — master-admin queue of self-serve free-audit
// submissions from /free-audit. Read-only listing + drill-in modal for
// transcripts. Public intake stores audio bytes only during transcription;
// once processed, only text is retained.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { Mail, RefreshCw, Loader2, X, CheckCircle2, Clock, AlertCircle, User, Building2, Phone } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  queued: { icon: Clock, label: 'Queued', tone: 'text-amber-300 bg-amber-500/15' },
  processed: { icon: CheckCircle2, label: 'Processed', tone: 'text-teal-300 bg-teal-500/15' },
  failed: { icon: AlertCircle, label: 'Failed', tone: 'text-red-300 bg-red-500/15' },
};

const timeAgo = (iso) => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const AdminAuditIntakesPage = () => {
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/audit-intakes?limit=100`, { headers });
      setRows(r.data.intakes || []);
    } catch (e) {
      console.error('load audit intakes failed', e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  const openDetail = useCallback(async (id) => {
    setDetailLoading(true);
    try {
      const r = await axios.get(`${API}/admin/audit-intakes/${id}`, { headers });
      setDetail(r.data);
    } catch (e) {
      console.error('load detail failed', e);
    } finally {
      setDetailLoading(false);
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  return (
    <AdminLayout>
    <div className="p-6 max-w-6xl mx-auto" data-testid="admin-audit-intakes-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Mail className="w-6 h-6 text-teal-400" />
            Free Audit Intakes
          </h1>
          <p className="text-sm text-white/60 mt-1">
            Submissions from the public <code className="text-xs bg-white/10 px-1.5 py-0.5 rounded">/free-audit</code> form.
            Transcripts are generated in the background; audio is deleted after processing.
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10 transition-colors"
          data-testid="refresh-audit-intakes-btn"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Refresh
        </button>
      </div>

      {loading && rows.length === 0 ? (
        <div className="text-center py-12 text-white/50">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white/5 border border-white/10 rounded-xl p-12 text-center">
          <Mail className="w-8 h-8 text-white/30 mx-auto mb-3" />
          <p className="text-white/60 text-sm">No audit intakes yet. Once someone uploads calls at <code>/free-audit</code>, they&apos;ll appear here.</p>
        </div>
      ) : (
        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-white/60 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">Submitted</th>
                <th className="px-4 py-3 text-left">Contact</th>
                <th className="px-4 py-3 text-left">Company / role</th>
                <th className="px-4 py-3 text-center">Files</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {rows.map((r) => {
                const meta = STATUS_META[r.status] || STATUS_META.queued;
                const Icon = meta.icon;
                return (
                  <tr key={r.id} className="hover:bg-white/5" data-testid={`audit-intake-row-${r.id}`}>
                    <td className="px-4 py-3 text-white/50 whitespace-nowrap" title={r.created_at}>{timeAgo(r.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="text-white font-medium">{r.name}</div>
                      <a href={`mailto:${r.email}`} className="text-teal-300 hover:underline text-xs">{r.email}</a>
                      {r.phone && <div className="text-white/50 text-xs">{r.phone}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-white">{r.company}</div>
                      <div className="text-white/50 text-xs capitalize">{r.role}</div>
                    </td>
                    <td className="px-4 py-3 text-center text-white/80 font-mono">{r.file_count}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${meta.tone}`}>
                        <Icon className="w-3 h-3" />
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => openDetail(r.id)}
                        className="text-teal-300 hover:text-teal-200 text-sm font-semibold"
                        data-testid={`audit-intake-view-${r.id}`}
                      >
                        View →
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Drill-in modal */}
      {detail && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="audit-intake-detail-modal">
            <div className="p-6 border-b border-slate-100 flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Audit intake · {detail.id}</h2>
                <p className="text-sm text-slate-500 mt-1">{timeAgo(detail.created_at)} · {detail.file_count} call{detail.file_count === 1 ? '' : 's'}</p>
              </div>
              <button onClick={() => setDetail(null)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400" data-testid="close-audit-detail">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto space-y-6">
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div className="flex items-start gap-2">
                  <User className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div>
                    <div className="text-slate-900 font-medium">{detail.name}</div>
                    <a href={`mailto:${detail.email}`} className="text-teal-700 hover:underline text-xs">{detail.email}</a>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Building2 className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div>
                    <div className="text-slate-900">{detail.company}</div>
                    <div className="text-slate-500 text-xs capitalize">{detail.role}</div>
                  </div>
                </div>
                {detail.phone && (
                  <div className="flex items-start gap-2">
                    <Phone className="w-4 h-4 text-slate-400 mt-0.5" />
                    <div className="text-slate-900">{detail.phone}</div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                {(detail.files || []).map((f, i) => (
                  <div key={i} className="border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-semibold text-slate-900">Call {i + 1} · {f.original_name}</div>
                      {f.sentiment && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${f.sentiment === 'positive' ? 'bg-teal-100 text-teal-700' : f.sentiment === 'negative' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>
                          {f.sentiment}
                        </span>
                      )}
                    </div>
                    {f.summary && (
                      <div className="mt-2">
                        <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1">Summary</div>
                        <p className="text-sm text-slate-700">{f.summary}</p>
                      </div>
                    )}
                    {f.top_focus && (
                      <div className="mt-3">
                        <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1">Top coaching focus</div>
                        <p className="text-sm text-slate-700">{f.top_focus}</p>
                      </div>
                    )}
                    {f.transcript && (
                      <details className="mt-3">
                        <summary className="cursor-pointer text-xs text-teal-700 font-semibold">View full transcript ({f.transcript.length} chars)</summary>
                        <pre className="mt-2 bg-slate-50 border border-slate-200 rounded p-3 text-xs text-slate-700 whitespace-pre-wrap max-h-64 overflow-y-auto">{f.transcript}</pre>
                      </details>
                    )}
                  </div>
                ))}
              </div>
            </div>
            {detailLoading && <div className="p-4 text-center text-slate-500 text-sm">Loading…</div>}
          </div>
        </div>
      )}
    </div>
    </AdminLayout>
  );
};

export default AdminAuditIntakesPage;
